import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MapPinned, Users, FileText, CheckSquare, Activity, Map } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import api from "../lib/api";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

// Fix Leaflet Default Icon
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});
L.Marker.prototype.options.icon = DefaultIcon;

// Ottawa Center
const OTTAWA_CENTER = [45.4215, -75.6972];

// Helper component to programmatically change map view
function ChangeView({ center, zoom }) {
    const map = useMap();
    useEffect(() => {
        map.setView(center, zoom);
    }, [center, zoom, map]);
    return null;
}

export default function GeoAnalytics() {
    const [stats, setStats] = useState([]);
    const [caseworkerStats, setCaseworkerStats] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        try {
            const res = await api.getGeoStats();
            // Backend returns { _id: postal_code, count, lat, lon }
            const formatted = res.data.map((item) => ({
                name: item._id || "Unknown",
                value: item.count,
                lat: item.lat,
                lon: item.lon,
            }));
            setStats(formatted);

            // Load caseworker details for insights if available from dashboard stats
            // Ideally we should have a dedicated endpoint, but let's reuse dashboard stats for now
            const dashboardRes = await api.getDashboardStats();
            if (dashboardRes.data?.user_activity) {
                setCaseworkerStats(dashboardRes.data.user_activity);
            }

        } catch (error) {
            console.error("Failed to load analytics", error);
        } finally {
            setLoading(false);
        }
    };

    const COLORS = ["#0ea5e9", "#22c55e", "#eab308", "#f97316", "#ef4444", "#8b5cf6"];
    const mapData = stats.filter((s) => s.lat && s.lon);

    // Calculate bounds or center
    // If we have data, we could fitBounds, but user requested Ottawa area.
    // We'll stick to Ottawa center but maybe adjust zoom if we have points.

    return (
        <div className="min-h-screen bg-slate-50">
            <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
                <div className="max-w-6xl mx-auto px-6 py-4">
                    <div className="flex items-center gap-4">
                        <Link to="/">
                            <Button variant="ghost" size="sm" className="rounded-sm">
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back to Dashboard
                            </Button>
                        </Link>
                        <div>
                            <h1 className="text-xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-2">
                                <Activity className="w-5 h-5 text-blue-600" />
                                Analytics & Insights
                            </h1>
                            <p className="text-sm text-slate-500">Geographic distribution and caseworker performance</p>
                        </div>
                        <div className="ml-auto">
                            <LanguageSwitcher />
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">

                {/* Geographic Section */}
                <div className="grid lg:grid-cols-3 gap-6">
                    <Card className="col-span-2 border-slate-200">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Map className="w-4 h-4" /> Assessment Distribution (Ottawa Region)
                            </CardTitle>
                            <CardDescription>
                                {mapData.length > 0
                                    ? `Displaying ${mapData.length} active postal code regions`
                                    : "No geocoded data available yet"}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="p-0 sm:p-6 pt-0">
                            <div className="h-[450px] w-full rounded-md overflow-hidden border border-slate-200 relative z-0">
                                <MapContainer
                                    center={OTTAWA_CENTER}
                                    zoom={11}
                                    style={{ height: "100%", width: "100%" }}
                                    scrollWheelZoom={true}
                                >
                                    <ChangeView center={OTTAWA_CENTER} zoom={11} />
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    {mapData.map((item, index) => (
                                        <Marker key={index} position={[item.lat, item.lon]}>
                                            <Popup>
                                                <div className="text-center min-w-[120px]">
                                                    <p className="font-bold text-base mb-1">{item.name}</p>
                                                    <Badge variant="secondary" className="mb-1">{item.value} Case{item.value !== 1 ? "s" : ""}</Badge>
                                                    <p className="text-xs text-slate-500">Postal Code Region</p>
                                                </div>
                                            </Popup>
                                        </Marker>
                                    ))}
                                </MapContainer>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-slate-200">
                        <CardHeader>
                            <CardTitle>Regional Breakdown</CardTitle>
                            <CardDescription>Cases by Postal Code</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="h-[200px] flex items-center justify-center text-sm text-slate-500">Loading data...</div>
                            ) : stats.length === 0 ? (
                                <div className="py-8 text-center text-slate-500 text-sm">No location data found</div>
                            ) : (
                                <div className="h-[350px] w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={stats} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                                            <XAxis type="number" hide />
                                            <YAxis dataKey="name" type="category" width={50} tick={{ fontSize: 11 }} />
                                            <Tooltip cursor={{ fill: 'transparent' }} />
                                            <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
                                                {stats.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Caseworker Performance Section */}
                <h2 className="text-lg font-bold text-slate-800 font-['Manrope'] mt-8 flex items-center gap-2">
                    <Users className="w-5 h-5" /> Caseworker Performance Insights
                </h2>

                <div className="grid md:grid-cols-3 gap-6">
                    {/* Activity Leaderboard */}
                    <Card className="md:col-span-2 border-slate-200">
                        <CardHeader>
                            <CardTitle>Activity Leaderboard</CardTitle>
                            <CardDescription>Top performing caseworkers by case volume</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {caseworkerStats
                                    .sort((a, b) => b.cases_created - a.cases_created)
                                    .slice(0, 5)
                                    .map((worker, i) => (
                                        <div key={worker.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100">
                                            <div className="flex items-center gap-4">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${i === 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-200 text-slate-600'}`}>
                                                    {i + 1}
                                                </div>
                                                <div>
                                                    <div className="font-semibold text-slate-900">{worker.name}</div>
                                                    <div className="text-xs text-slate-500">{worker.email}</div>
                                                </div>
                                            </div>
                                            <div className="flex gap-4 text-sm">
                                                <div className="text-center">
                                                    <div className="font-bold text-slate-900">{worker.cases_created}</div>
                                                    <div className="text-xs text-slate-500">Cases</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="font-bold text-green-600">{worker.cases_submitted}</div>
                                                    <div className="text-xs text-slate-500">Submitted</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                {caseworkerStats.length === 0 && !loading && (
                                    <div className="text-center py-8 text-slate-500">No caseworker data available</div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Summary Stats */}
                    <Card className="border-slate-200 bg-slate-800 text-white">
                        <CardHeader>
                            <CardTitle className="text-slate-100">Team Overview</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div>
                                <div className="text-3xl font-bold">{caseworkerStats.length}</div>
                                <div className="text-sm text-slate-400">Active Caseworkers</div>
                            </div>
                            <div className="h-px bg-slate-700" />
                            <div>
                                <div className="text-3xl font-bold">
                                    {caseworkerStats.reduce((acc, curr) => acc + curr.cases_created, 0)}
                                </div>
                                <div className="text-sm text-slate-400">Total Assessments Created</div>
                            </div>
                            <div className="h-px bg-slate-700" />
                            <div>
                                <div className="text-3xl font-bold text-green-400">
                                    {caseworkerStats.reduce((acc, curr) => acc + curr.cases_submitted, 0)}
                                </div>
                                <div className="text-sm text-slate-400">Assessments Submitted</div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </main>
        </div>
    );
}
